import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {MODEL_INPUT_TYPE} from 'model-hub/scientific-models/data-first/CustomEntityInputTypeDefinitions';
import {
  MODEL_CATEGORY,
  ScientificModelNode,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';

const REQUIRED_TEXT_INPUT_DATASET_COLUMN: GetPipelineConfigSpecType<'columnReference'> = {
  kind: 'columnReference',
  required: true,
  inputDataName: 'inputDataset',
  allowMissingColumn: false,
  validationOptions: {
    allowedLeafTypes: [LeafType.TEXT],
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
};

export interface ModelInputField {
  key: string;
  displayName: string;
  spec: PipelineConfigJsonSpec;
}

/** Keep in sync with scientific_model_job_config_builder.py (`USE_MSA`). */
export const USE_MSA_KEY = 'useMsa';
export const USE_MSA_DISPLAY_NAME = 'Use MSAs';

// Keep in sync with scientific_model_job_config_builder.py, which reads these keys for the same
// model capabilities.
export function getModelInputFields(model: ScientificModelNode): ReadonlyArray<ModelInputField> | null {
  if (model.category === MODEL_CATEGORY.PROPERTY_PREDICTION) {
    if (model.inputTypes.includes(MODEL_INPUT_TYPE.ANTIBODY)) {
      return [
        {
          key: 'heavyChainColumn',
          displayName: 'Heavy-chain sequence column',
          spec: REQUIRED_TEXT_INPUT_DATASET_COLUMN,
        },
        {
          key: 'lightChainColumn',
          displayName: 'Light-chain sequence column',
          spec: REQUIRED_TEXT_INPUT_DATASET_COLUMN,
        },
      ];
    }
    if (model.inputTypes.includes(MODEL_INPUT_TYPE.MOLECULE)) {
      return [{key: 'smilesColumn', displayName: 'SMILES column', spec: REQUIRED_TEXT_INPUT_DATASET_COLUMN}];
    }
    return null;
  }
  if (model.category === MODEL_CATEGORY.STRUCTURE_PREDICTION) {
    return getStructurePredictionInputFields(model);
  }
  return null;
}

function getStructurePredictionInputFields(model: ScientificModelNode): ReadonlyArray<ModelInputField> {
  const {supportsMultimers, supportsMultimerMolecules} = model.capabilities;
  const fields: Array<ModelInputField> = [
    {
      key: 'sequenceColumns',
      displayName: supportsMultimers ? 'Protein sequence columns' : 'Protein sequence column',
      spec: {
        kind: 'array',
        required: true,
        elementSpec: REQUIRED_TEXT_INPUT_DATASET_COLUMN,
        minItems: 1,
        ...(supportsMultimers ? {} : {maxItems: 1}),
      },
    },
  ];
  if (supportsMultimers && supportsMultimerMolecules) {
    fields.push({
      key: 'ligandColumns',
      displayName: 'Ligand SMILES columns',
      spec: {kind: 'array', required: false, elementSpec: REQUIRED_TEXT_INPUT_DATASET_COLUMN},
    });
  }
  if (supportsMSA(model)) {
    fields.push({
      key: USE_MSA_KEY,
      displayName: USE_MSA_DISPLAY_NAME,
      spec: {kind: 'boolean', required: false},
    });
  }
  return fields;
}
